import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabaseClient';

const ChatDashboard = ({ user }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [error, setError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [typingUsers, setTypingUsers] = useState([]);
  const [lastSent, setLastSent] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [darkMode, setDarkMode] = useState(true);
  const [expandedMessage, setExpandedMessage] = useState(null);

  const messagesEndRef = useRef(null);
  const typingChannelRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Auto-scroll when messages update
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Setup typing indicator channel
  useEffect(() => {
    if (!user) return;

    const channel = supabase.channel('typing_indicator', {
      config: { presence: { key: user.id } },
    });

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        const users = Object.values(state).flat().filter((u) => u.user_id !== user.id);
        setTypingUsers(users.filter((u) => u.typing));
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            user_id: user.id,
            username: user.email?.split('@')[0] || 'Anonymous',
            typing: false,
          });
        }
      });

    typingChannelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Update typing status with debouncing
  const handleTyping = useCallback((text) => {
    setNewMessage(text);
    
    if (!typingChannelRef.current) return;
    
    // Update typing status
    typingChannelRef.current.track({
      user_id: user?.id,
      username: user?.email?.split('@')[0] || 'Anonymous',
      typing: text.length > 0,
    });
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set timeout to stop typing indicator after 1 second of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      if (typingChannelRef.current) {
        typingChannelRef.current.track({
          user_id: user?.id,
          username: user?.email?.split('@')[0] || 'Anonymous',
          typing: false,
        });
      }
    }, 1000);
  }, [user]);

  // Fetch messages + real-time updates
  useEffect(() => {
    const fetchMessages = async () => {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Fetch error:', error);
        setError('Failed to load messages');
      } else {
        setMessages(data);
      }
    };

    fetchMessages();

    const subscription = supabase
      .channel('public_chat')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chat_messages' },
        (payload) => {
          setMessages((prev) => [...prev, payload.new]);
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'chat_messages' },
        (payload) => {
          setMessages((prev) => prev.filter(msg => msg.id !== payload.old.id));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, []);

  // Send new message
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!newMessage.trim()) return;
    setIsSending(true);

    try {
      const {
        data: { user: currentUser },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !currentUser) {
        throw new Error('Please login to chat');
      }

      // Construct the message locally
      const optimisticMsg = {
        id: Date.now(), // temporary unique key
        user_id: currentUser.id,
        username: currentUser.email?.split('@')[0] || 'Anonymous',
        message: newMessage,
        created_at: new Date().toISOString(),
      };

      // Optimistically update UI
      setMessages((prev) => [...prev, optimisticMsg]);

      const { error: insertError } = await supabase.from('chat_messages').insert([
        {
          user_id: currentUser.id,
          username: optimisticMsg.username,
          message: newMessage,
        },
      ]);

      if (insertError) throw insertError;

      setNewMessage('');
      setLastSent('Message sent! 🎉');
      setTimeout(() => setLastSent(null), 1500);
      
      // Stop typing indicator after sending
      if (typingChannelRef.current) {
        typingChannelRef.current.track({
          user_id: user?.id,
          username: user?.email?.split('@')[0] || 'Anonymous',
          typing: false,
        });
      }
    } catch (err) {
      console.error('Send error:', err);
      setError(err.message);
    } finally {
      setIsSending(false);
    }
  };

  // Delete a message
  const handleDeleteMessage = async (messageId) => {
    try {
      const { error } = await supabase
        .from('chat_messages')
        .delete()
        .eq('id', messageId);

      if (error) throw error;
      
      setShowDeleteConfirm(null); // Close confirmation dialog
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete message');
    }
  };

  // Toggle message expansion
  const toggleMessageExpansion = (messageId) => {
    setExpandedMessage(expandedMessage === messageId ? null : messageId);
  };

  // Toggle theme
  const toggleTheme = () => {
    setDarkMode(!darkMode);
  };

  return (
    <div className={`chat-container ${darkMode ? 'dark-theme' : 'light-theme'}`}>
      <div className="chat-header">
        <h2>Community Chat</h2>
        <div className="header-controls">
          <div className={`connection-status ${isOnline ? 'online' : 'offline'}`}>
            {isOnline ? '🟢 Online' : '🔴 Offline'}
          </div>
          <button className="theme-toggle" onClick={toggleTheme}>
            {darkMode ? '☀️' : '🌙'}
          </button>
        </div>
      </div>

      {error && (
        <div className="chat-error">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      <div className="messages-list">
        {messages.length > 0 ? (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`message ${msg.user_id === user?.id ? 'own-message' : ''} ${expandedMessage === msg.id ? 'expanded' : ''}`}
              onClick={() => toggleMessageExpansion(msg.id)}
            >
              <div className="message-header">
                <span className="username">{msg.username}</span>
                <span className="timestamp">
                  {new Date(msg.created_at).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
                
                {/* Message actions (delete only) */}
                <div className="message-actions" onClick={(e) => e.stopPropagation()}>
                  {/* Show delete button only for user's own messages */}
                  {msg.user_id === user?.id && (
                    <button 
                      className="delete-btn"
                      onClick={() => setShowDeleteConfirm(msg.id)}
                      title="Delete this message"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
              
              <div className="message-content">{msg.message}</div>
              
              {/* Delete confirmation dialog */}
              {showDeleteConfirm === msg.id && (
                <div className="delete-confirmation" onClick={(e) => e.stopPropagation()}>
                  <p>Delete this message?</p>
                  <div className="confirmation-buttons">
                    <button onClick={() => handleDeleteMessage(msg.id)}>Yes</button>
                    <button onClick={() => setShowDeleteConfirm(null)}>No</button>
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <p className="no-messages">No messages yet. Start the conversation!</p>
        )}
        <div ref={messagesEndRef} />
      </div>

      {typingUsers.length > 0 && (
        <div className="typing-indicator">
          {typingUsers.slice(0, 3).map((u) => u.username).join(', ')}
          {typingUsers.length > 3 ? ' and others' : ''}{' '}
          {typingUsers.length > 1 ? 'are' : 'is'} typing...
        </div>
      )}

      <form onSubmit={handleSubmit} className="message-form">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => handleTyping(e.target.value)}
          placeholder="Type your message..."
          disabled={isSending}
          aria-label="Chat message input"
        />
        <button type="submit" disabled={isSending || !newMessage.trim()} aria-label="Send message">
          {isSending ? 'Sending...' : 'Send'}
        </button>
      </form>

      {lastSent && (
        <div className="sent-confirmation">
          ✅ {lastSent}
        </div>
      )}
    </div>
  );
};

export default ChatDashboard;